import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Dropdown.css';
import arrowDown from '../../../../src/assets/arrow-down.svg';

// Temporary debug version with inline styles to bypass CSS issues
function UnifiedDropdown({
  type,
  selectedValue,
  setSelectedValue,
  options,
  displayConfig = {},
  availabilityData = {},
  displayMapping = {}
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const searchInputRef = useRef(null);

  const getPlaceholderText = () => {
    const placeholders = {
      site: "Select a site",
      dateRange: "Select a date range",
      device: "Select a device",
      deck: "Select a deck",
      business: "Select a business",
    };
    return displayConfig.placeholder || placeholders[type] || "Select an option";
  };

  const handleSelect = (value) => {
    console.log(`UnifiedDropdown.handleSelect (${type}):`, { value, selectedValue, hasData: checkHasData(value) });
    const hasData = checkHasData(value);

    // Always allow selection if:
    // 1. It's already selected, OR
    // 2. It has data, OR
    // 3. It's the "all" option (All Sites, All Devices, All Decks, etc.)
    if (value === selectedValue || hasData || value === 'all') {
      console.log(`  → Calling setSelectedValue(${value})`);
      setSelectedValue(value);
      setIsOpen(false);
      setSearchTerm('');
    } else {
      console.log(`  ✗ Selection blocked: hasData=${hasData}, isAll=${value === 'all'}, isSelected=${value === selectedValue}`);
    }
  };

  const checkHasData = (value) => {
    // Always allow "all" options to be selectable
    if (value === 'all') {
      return true;
    }

    if (!availabilityData || Object.keys(availabilityData).length === 0) {
      return true;
    }

    switch (type) {
      case 'site':
        return availabilityData[value] !== false;
      case 'dateRange':
        return availabilityData[value]?.length > 0;
      case 'device':
        return availabilityData[value] !== false;
      case 'business':
        return true;
      default:
        return true;
    }
  };

  const getDisplayName = (value) => {
    switch (type) {
      case 'site':
        return displayMapping[value] || `Site ${value}`;
      case 'dateRange':
        return options.find(option => option.value === value)?.label || value;
      case 'device':
        return displayMapping[value] || `Device ${value}`;
      case 'deck':
        return displayMapping[value] || `Deck ${value}`;
      case 'business':
        return displayMapping[value] || `Business ${value}`;
      default:
        return value;
    }
  };

  // Enhanced filtering and sorting logic
  const getFilteredOptions = () => {
    let filteredOptions = options;

    // Apply search filter for site type
    if (type === 'site' && searchTerm.trim()) {
      filteredOptions = options.filter(value => {
        const displayName = getDisplayName(value).toLowerCase();
        return displayName.includes(searchTerm.toLowerCase());
      });
    }

    // Sort alphabetically and by data availability (sites with data first)
    // "all" option always appears at the top
    if (type === 'site') {
      filteredOptions = [...filteredOptions].sort((a, b) => {
        // First priority: "all" option always at the top
        if (a === 'all') return -1;
        if (b === 'all') return 1;

        const aHasData = checkHasData(a);
        const bHasData = checkHasData(b);
        const aDisplayName = getDisplayName(a).toLowerCase();
        const bDisplayName = getDisplayName(b).toLowerCase();

        // Then sort by data availability (sites with data come first)
        if (aHasData && !bHasData) return -1;
        if (!aHasData && bHasData) return 1;

        // Finally sort alphabetically
        return aDisplayName.localeCompare(bDisplayName);
      });
    }

    return filteredOptions;
  };

  // Clear search function
  const clearSearch = () => {
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    // Calculate menu position when opening
    if (newIsOpen && toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4, // 4px gap below button
        left: rect.left,
        width: rect.width
      });
    }

    if (newIsOpen && type === 'site') {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleClickOutside = (event) => {
    // Check if click is inside the dropdown wrapper
    const isClickInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);

    // Check if click is inside the portal menu (which is outside the DOM tree)
    const isClickInsidePortal = event.target?.closest('[data-dropdown-menu="true"]') !== null;

    // Only close if click is outside both the dropdown and portal menu
    if (!isClickInsideDropdown && !isClickInsidePortal) {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStatusIndicator = (value) => {
    if (type !== 'site' || !availabilityData[value]) return null;
    
    const status = availabilityData[value];
    if (status === 'online') return <span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', display: 'inline-block'}}></span>;
    if (status === 'offline') return <span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', display: 'inline-block'}}></span>;
    if (status === 'warning') return <span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b', display: 'inline-block'}}></span>;
    return null;
  };

  const renderOptions = () => {
    const filteredOptions = getFilteredOptions();
    
    // For non-site dropdowns, show the old behavior
    if (type !== 'site' && (!filteredOptions || filteredOptions.length === 0)) {
      return (
        <div style={{
          padding: '12px 16px',
          color: '#94a3b8',
          cursor: 'not-allowed'
        }}>
          <span>No options available</span>
        </div>
      );
    }
    
    switch (type) {
      case 'dateRange':
        return filteredOptions.map(option => {
          const hasData = checkHasData(option.value);
          
          return (
            <div 
              key={option.value} 
              style={{
                padding: '12px 16px',
                cursor: hasData ? 'pointer' : 'not-allowed',
                color: hasData ? '#334155' : '#94a3b8',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: selectedValue === option.value ? '#f8fafc' : 'transparent'
              }}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={(e) => {
                if (hasData) e.target.style.backgroundColor = '#667eea';
                if (hasData) e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = selectedValue === option.value ? '#f8fafc' : 'transparent';
                e.target.style.color = hasData ? '#334155' : '#94a3b8';
              }}
            >
              <span>{option.label}</span>
              {!hasData && <span style={{
                backgroundColor: '#f1f5f9',
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: '500',
                padding: '3px 8px',
                borderRadius: '12px'
              }}>No data</span>}
            </div>
          );
        });
      
      case 'site':
        return (
          <>
            <div style={{
              position: 'sticky',
              top: 0,
              background: 'white',
              padding: '8px',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div style={{ position: 'relative' }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search sites..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '8px 32px 8px 12px', // Add right padding for X button
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    boxSizing: 'border-box'
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSearch();
                    }}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: '#94a3b8',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '3px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f1f5f9';
                      e.target.style.color = '#64748b';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#94a3b8';
                    }}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            {filteredOptions.length === 0 && searchTerm ? (
              <div style={{
                padding: '12px 16px',
                color: '#94a3b8',
                cursor: 'not-allowed'
              }}>
                <span>No sites found</span>
              </div>
            ) : (
              filteredOptions.map(value => {
              const hasData = checkHasData(value);
              const statusIndicator = getStatusIndicator(value);
              
              return (
                <div 
                  key={value} 
                  style={{
                    padding: '12px 16px',
                    cursor: hasData ? 'pointer' : 'not-allowed',
                    color: hasData ? '#334155' : '#94a3b8',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: selectedValue === value ? '#f8fafc' : 'transparent'
                  }}
                  onClick={() => handleSelect(value)}
                  onMouseEnter={(e) => {
                    if (hasData) e.target.style.backgroundColor = '#667eea';
                    if (hasData) e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = selectedValue === value ? '#f8fafc' : 'transparent';
                    e.target.style.color = hasData ? '#334155' : '#94a3b8';
                  }}
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    {statusIndicator}
                    <span>{getDisplayName(value)}</span>
                  </div>
                  {!hasData && <span style={{
                    backgroundColor: '#f1f5f9',
                    color: '#64748b',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    padding: '3px 8px',
                    borderRadius: '12px'
                  }}>No data</span>}
                </div>
              );
            })
            )}
          </>
        );
      
      case 'device':
      default:
        return filteredOptions.map(value => {
          const hasData = checkHasData(value);
          
          return (
            <div 
              key={value} 
              style={{
                padding: '12px 16px',
                cursor: hasData ? 'pointer' : 'not-allowed',
                color: hasData ? '#334155' : '#94a3b8',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: selectedValue === value ? '#f8fafc' : 'transparent'
              }}
              onClick={() => handleSelect(value)}
              onMouseEnter={(e) => {
                if (hasData) e.target.style.backgroundColor = '#667eea';
                if (hasData) e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = selectedValue === value ? '#f8fafc' : 'transparent';
                e.target.style.color = hasData ? '#334155' : '#94a3b8';
              }}
            >
              <span>{getDisplayName(value)}</span>
              {!hasData && <span style={{
                backgroundColor: '#f1f5f9',
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: '500',
                padding: '3px 8px',
                borderRadius: '12px'
              }}>No data</span>}
            </div>
          );
        });
    }
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: '100%',
        maxWidth: '100%'
      }}
    >
      <div
        ref={toggleRef}
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 10px',
          border: `2px solid ${isOpen ? '#667eea' : '#e2e8f0'}`,
          borderRadius: '8px',
          backgroundColor: 'white',
          cursor: 'pointer',
          minWidth: displayConfig.minWidth || '120px',
          boxSizing: 'border-box',
          color: '#334155',
          fontSize: '0.8rem',
          boxShadow: isOpen ? '0 0 0 3px rgba(102, 126, 234, 0.1)' : 'none'
        }}
      >
        <span style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1,
          minWidth: 0
        }}>
          {selectedValue ? getDisplayName(selectedValue) : getPlaceholderText()}
        </span>
        <img 
          src={arrowDown} 
          style={{
            width: '12px',
            height: '12px',
            marginLeft: '8px',
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            opacity: 0.6
          }}
          alt="Dropdown arrow" 
        />
      </div>
      
      {isOpen && ReactDOM.createPortal(
        <div
          data-dropdown-menu="true"
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            width: `${menuPosition.width}px`,
            background: 'white',
            border: '2px solid #667eea',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            zIndex: 100000, // Very high z-index to appear above carousel and page
            maxHeight: '400px',
            overflowY: 'auto',
            boxSizing: 'border-box'
          }}>
          {renderOptions()}
        </div>,
        document.body
      )}
    </div>
  );
}

export default UnifiedDropdown;